'use client'

import type { FunnelPayload } from '../svs-types'
import { resolveColor } from '../color-mapping'

interface FunnelRendererProps {
  payload: FunnelPayload
}

/**
 * Custom funnel renderer using CSS trapezoids.
 * Recharts FunnelChart exists but has limited styling control.
 * This gives us full Tailwind control.
 */
export function FunnelRenderer({ payload }: FunnelRendererProps) {
  const { items } = payload.data
  const options = payload.options || {}
  const maxValue = items.length > 0 ? Math.max(...items.map(i => i.value)) : 1
  const firstValue = items[0]?.value || 1

  return (
    <div className="flex flex-col items-center gap-1">
      {items.map((item, i) => {
        const widthPercent = (item.value / maxValue) * 100
        const color = resolveColor(item.color, i)
        const prevValue = i > 0 ? items[i - 1].value : firstValue
        const pctOfFirst = firstValue > 0 ? ((item.value / firstValue) * 100).toFixed(1) : '0'
        const pctOfPrev = prevValue > 0 ? ((item.value / prevValue) * 100).toFixed(1) : '0'

        return (
          <div key={i} className="flex w-full items-center gap-3">
            <div
              className="mx-auto flex items-center justify-center rounded px-4 py-2.5 text-sm font-medium text-white transition-all"
              style={{
                width: `${Math.max(widthPercent, 20)}%`,
                backgroundColor: color,
              }}
            >
              <span className="truncate">{item.label}</span>
              <span className="ml-2 opacity-80">{item.value.toLocaleString()}</span>
            </div>
            {options.showPercentages && i > 0 && (
              <span className="shrink-0 text-xs text-muted-foreground">
                {options.percentageBase === 'previous'
                  ? `${pctOfPrev}% of prev`
                  : `${pctOfFirst}%`}
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}
